import Component from "@glimmer/component";
import ENV from "glesys-controlpanel/config/environment";
import { service } from "@ember/service";
import { dropTask, restartableTask, timeout } from "ember-concurrency";
import { tracked } from "@glimmer/tracking";
import { action } from "@ember/object";
import { saveRecord } from "@warp-drive/legacy/compat/builders";

export default class ModalsServerReconfigureKvm extends Component {
  @service ajax;
  @service modal;
  @service abilities;
  @service store;

  @tracked serverCost = { price: this.server.billing?.price, total: this.server.billing?.total };
  @tracked cpuValue = this.server.cpucores;
  @tracked memoryValue = this.server.memorysize;
  @tracked diskValue = this.server.disksize;

  get project() {
    return this.args.params.project;
  }

  get server() {
    return this.args.params.server;
  }

  get task() {
    return this.args.params.task;
  }

  get cpuValues() {
    return this.server.platformArguments.get("cpuCores").map((core) => core.value);
  }

  get memoryValues() {
    return this.server.platformArguments.get("memorySizes").map((size) => size.value);
  }

  get diskValues() {
    return this.server.platformArguments.get("diskSizes").map((size) => size.value);
  }

  get filteredDiskValues() {
    let initialDiskSize = this.diskHasChanged ? this.server.disksize : this.diskValue;
    return this.diskValues.filter((value) => initialDiskSize <= value);
  }

  get supportsEditMemorySize() {
    return this.server.supportsEditMemorySize;
  }

  get supportsEditDiskSize() {
    return this.server.supportsEditDiskSize;
  }

  get shouldNotShowStorage() {
    return this.filteredDiskValues.length === 1;
  }

  get diskHasChanged() {
    return this.server.disksize !== this.diskValue;
  }

  get memoryHasChanged() {
    return this.server.memorysize !== this.memoryValue;
  }

  get cpuHasChanged() {
    return this.server.cpucores !== this.cpuValue;
  }

  get serverAttributesHasChanged() {
    return this.diskHasChanged || this.memoryHasChanged || this.cpuHasChanged;
  }

  get hasResellerPricing() {
    return this.abilities.cannot("access pricing for organization", this.project.organization);
  }

  getKVMServerCostTask = restartableTask(async () => {
    if (this.hasResellerPricing) {
      return;
    }

    await timeout(ENV.environment === "test" ? 0 : 250);

    let url = `${ENV.apiBaseUri}/server/estimatedcost`;
    let data = {
      projectkey: this.project?.get("id"),
      serverid: this.server.id,
      cpucores: this.cpuValue,
      disksize: this.diskValue,
      memorysize: this.memoryValue,
    };

    try {
      let response = await this.ajax.post(url, { data });
      this.serverCost = {
        price: response.billing.price,
        total: response.billing.total,
      };
    } catch {
      this.serverCost = {
        price: null,
      };
    }
  });

  saveServerReconfigurationTask = dropTask(async () => {
    let server = this.server;
    let config = {
      disksize: this.diskValue,
      memorysize: this.memoryValue,
      cpucores: this.cpuValue,
    };
    server.setProperties(config);
    await this.store.request(saveRecord(server));

    this.modal.close();
  });

  @action
  cpuValueChange(value) {
    this.cpuValue = value;
    this.getKVMServerCostTask.perform();
  }

  @action
  memoryValueChange(value) {
    this.memoryValue = value;
    this.getKVMServerCostTask.perform();
  }

  @action
  diskValueChange(value) {
    this.diskValue = value;
    this.getKVMServerCostTask.perform();
  }
}
